'use client';

import { useState, useEffect, useOptimistic, useTransition } from 'react';
import { Checkbox } from '@pixum/web-checkbox';
import { DPOAlertDialog } from './partials/DPOAlertDialog';
import { setDpoAction } from '../../../actions/setDpoAction';
import { trackDpoClick, trackDpoInfoClick } from '../../../analytics/overview';
import { logger } from 'utils';
import styles from './DPOCheckbox.module.css';
import Icon from '@pixum/icon';
import Button, { ButtonSizes, ButtonVariants } from '@pixum/button';

export interface DPOCheckboxProps {
  checked: boolean;
  dpoLabel: string;
  dpoInfo: string;
  okayLabel: string;
  moreInfoLabel: string;
  userId: string;
}

export function DPOCheckbox({ checked, dpoLabel, dpoInfo, okayLabel, moreInfoLabel, userId }: DPOCheckboxProps) {
  const [optimisticChecked, setOptimisticChecked] = useOptimistic(checked);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [, startTransition] = useTransition();

  /*
   * @pixum/web-checkbox generates random UUIDs for label/input id attrs on every render.
   * Gating on mounted prevents a server/client hydration mismatch — the Checkbox only
   * renders on the client where the UUID is generated once and is stable.
   */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleCheckboxChange = (isChecked: boolean) => {
    startTransition(async () => {
      setOptimisticChecked(isChecked);

      trackDpoClick(isChecked);

      const result = await setDpoAction({
        active: isChecked,
        userId
      });

      if (!result.success) {
        logger.error({ error: result.error }, 'Failed to update DPO consent');
      }
    });
  };

  const handleInfoClick = () => {
    trackDpoInfoClick();
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <section aria-label={dpoLabel} className={styles.dpoCheckboxWrapper}>
      {mounted && (
        <Checkbox
          dataTestId="dpo-checkbox"
          checked={optimisticChecked}
          label={dpoLabel}
          onChange={handleCheckboxChange}
        />
      )}

      <Button
        variant={ButtonVariants.TEXT}
        className={styles.infoButton}
        onClick={handleInfoClick}
        aria-label={`${dpoLabel} - ${moreInfoLabel}`}
        size={ButtonSizes.MEDIUM}
      >
        <Icon name="info-outline" />
      </Button>

      <DPOAlertDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        headline={dpoLabel}
        info={dpoInfo}
        okayLabel={okayLabel}
      />
    </section>
  );
}
